import { useCallback, useState } from 'react';
import { useRecoilState } from 'recoil';
import { licenseListAtom } from '../state/licenseAtoms';
import { licenseService } from '../services/licenseService';
import { normalizeLicense, normalizeLicenseList } from '../utils/normalizeLicense';
import { FALLBACK_LICENSES } from '../utils/licenseConstants';

function getFallbackLicenses(search = '') {
  const normalizedSearch = search.trim().toLowerCase();
  const fallback = normalizeLicenseList(FALLBACK_LICENSES);

  if (!normalizedSearch) return fallback;

  return fallback.filter((license) =>
    [license.name, license.alias, license.description]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(normalizedSearch))
  );
}

export default function useLicenseList() {
  const [listState, setListState] = useRecoilState(licenseListAtom);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isUsingFallback, setIsUsingFallback] = useState(false);

  const fetchLicenses = useCallback(
    async (search = '') => {
      setIsLoading(true);
      setError(null);

      try {
        const res = await licenseService.getLicenses({ search });

        if (res?.status === 200 || res?.status === 201) {
          const rawList = res?.data?.list ?? res?.data?.results ?? res?.data ?? [];
          const licenses = normalizeLicenseList(Array.isArray(rawList) ? rawList : []);
          const count = res?.data?.count ?? licenses.length;

          setListState({ licenses, count });
          setIsUsingFallback(false);
          return licenses;
        }

        throw new Error(res?.data?.message || 'Failed to fetch licenses');
      } catch {
        const fallbackLicenses = getFallbackLicenses(search);
        setListState({ licenses: fallbackLicenses, count: fallbackLicenses.length });
        setIsUsingFallback(true);
        setError('License API is unavailable. Showing sample license data.');
        return fallbackLicenses;
      } finally {
        setIsLoading(false);
      }
    },
    [setListState]
  );

  const prependLicense = useCallback(
    (rawLicense) => {
      const license = normalizeLicense(rawLicense);
      setListState((prev) => ({
        licenses: [license, ...prev.licenses],
        count: prev.count + 1,
      }));
      return license;
    },
    [setListState]
  );

  const updateLicenseInList = useCallback(
    (rawLicense) => {
      const license = normalizeLicense(rawLicense);
      setListState((prev) => ({
        ...prev,
        licenses: prev.licenses.map((item) => (item.id === license.id ? license : item)),
      }));
      return license;
    },
    [setListState]
  );

  const archiveLicenseInList = useCallback(
    (rawLicense) => {
      const license = normalizeLicense(rawLicense);
      setListState((prev) => ({
        ...prev,
        licenses: prev.licenses.map((item) => (item.id === license.id ? license : item)),
      }));
      return license;
    },
    [setListState]
  );

  return {
    licenses: listState.licenses,
    count: listState.count,
    isLoading,
    error,
    isUsingFallback,
    fetchLicenses,
    prependLicense,
    updateLicenseInList,
    archiveLicenseInList,
  };
}
